import Vue from "vue";
import axios from "axios";
import {reactive, toRefs, provide, inject} from "@vue/composition-api";
import {$t} from "@/i18n";
import {APP_STORE_APP_TYPES} from "@/helpers/constants";

export const state = reactive({
    next_invoice_number: 0,
    last_imported_at: null
});

export const actions = {

    fetchInvoice(id, show_all_lines) {
        return new Promise(function (resolve, reject) {
            let params = ''
            if (show_all_lines && parseInt(show_all_lines)) {
                params = '?show_all_lines=1'
            }
            let url = Vue.config.url_prefix + "/api/sales_invoice/{0}{1}".format(id, params);
            axios
                .get(url)
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },

    fetchSalesInvoices(params) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/list".format(params['account_id']);
            axios
                .get(url, {params: params})
                .then(resp => {
                    state.next_invoice_number = resp.data.next_invoice_number
                    state.last_imported_at = resp.data.last_imported_at
                    resolve(resp.data)
                })
                .catch(ex => reject(ex));
        });
    },

    saveSalesInvoice(invoice, force = false) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/edit".format(invoice.account_id);
            axios
                .post(url, invoice, {params: {force}})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    calculateSalesInvoices(account_id, invoice_id_list, invoice_status) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/calculate".format(account_id);
            axios
                .post(url, {invoice_id_list, invoice_status})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },

    cancelSalesInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/annul".format(account_id);
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    restoreSalesInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/restore".format(account_id);
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },

    markPaidInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = `/sales_invoices/${account_id}/set_paid`
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },

    markUnpaidInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = `/sales_invoices/${account_id}/set_unpaid`
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },

    clearProcessErrors(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/clear_process_status".format(account_id);
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    sendSalesInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/send".format(account_id);
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    deleteSalesInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/delete".format(account_id);
            axios
                .delete(url, {data: {invoice_id_list}})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    exportSalesInvoices(account_id, invoice_id_list) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/export".format(account_id);
            axios
                .post(url, {invoice_id_list})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    updateCompletedAt(row_id, invoice_id, is_completed) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/complete_invoice_row".format(invoice_id);
            axios
                .patch(url, {row_id, is_completed})
                .then(resp => resolve(resp.data))
                .catch(ex => reject(ex));
        });
    },
    async nextInvoiceNumber(company_id, next_invoice_number) {
        return new Promise(function (resolve, reject) {
            let url = Vue.config.url_prefix + "/api/sales_invoices/{0}/next_invoice_number/{1}/".format(company_id, next_invoice_number);
            axios
                .post(url, {})
                .then(resp => {
                    state.next_invoice_number = resp.next_invoice_number
                    resolve(resp.data)
                })
                .catch(ex => reject(ex));
        });
    }
};

export const getters = {
    getStatusColorMap: (status, process_status) => {
        let status_map = {
            "in_progress": {text: $t("Draft"), color: "grey--text text--darken-1"},
            "calculated": {text: $t("Approval"), color: "orange--text"},
            "pending": {text: $t("Creating invoice"), color: "blue--text"},
            "created": {text: $t("Created"), color: "blue--text"},
            "sent": {text: $t("Sent"), color: "blue--text"},
            "paid": {text: $t("Invoice paid"), color: "green--text"},
            "annulled": {text: $t("Annulled"), color: "black--text"},
            "processing": {text: $t("Processing"), color: "black--text"},
        }

        let data = {};
        data["color"] = status_map[status]["color"];
        data["text"] = $t(status_map[status]["text"]);
        if (process_status === 'send_invoice') data['text'] = $t('Sending invoice');
        if (process_status === 'create_invoice') data['text'] = $t('Creating invoice');
        if (process_status === 'export_invoice') data['text'] = $t('Exporting invoice');
        if (process_status === 'calculate_invoice') data['text'] = $t('Calculating invoice');
        if (process_status === 'import_invoice') data['text'] = $t('Importing invoice');
        return data;
    },

    getProcessErrorMessageForInvoice: (invoice) => {
        if (!invoice.process_status || !(invoice._data != null ? invoice._data.process_error : undefined)) {
            return null;
        } else if (invoice._data.process_error.code === 403) {
            return $t("Invalid Scoro credentials");
        } else if (invoice._data.process_error.messages) {
            return invoice._data.process_error.messages.join(", ");
        }
        return null;
    },
    getSendableInvoices: invoices => {
        return invoices.filter(invoice => {
            return (invoice.status === "created" || invoice.status === "sent" || invoice.status === "paid") && !invoice.process_status;
        });
    },
    getDeletableInvoices: invoices => {
        return invoices.filter(invoice => {
            return (invoice.status === "created" || invoice.status === "sent" || invoice.status === "annulled") && !invoice.process_status;
            // return invoice.status !== "calculated" && invoice.status !== "in_progress" && !invoice.process_status;
        });
    },
    getCreatableInvoices: invoices => {
        return invoices.filter(invoice => {
            return invoice.status === "calculated" || (invoice.status === "in_progress" && !invoice.process_status);
        });
    },
    getInProgressInvoices: invoices => {
        return invoices.filter(invoice => {
            return invoice.status === "in_progress";
        });
    },
    getExportableInvoices: invoices => {
        return invoices.filter(invoice => {
            return (invoice.status === "created" || invoice.status === "sent" || invoice.status === "paid") && !invoice.process_status;
        });
    },
    getUpdatableStatusInvoices: (invoices, vendorType) => {
        return invoices.filter(invoice => {
            if (!(invoice.status === "created" || invoice.status === "sent")) return false
            if (invoice.process_status) return false
            if (!invoice._data) return false

            let key = vendorType + '_id'
            if (!invoice._data[key]) return false
            return true
        });
    }
};

export const key = Symbol("SalesInvoiceModule");

export const provideSalesInvoice = () => {
    provide(key, {
        ...toRefs(state),
        ...getters,
        ...actions
    });
};

export default () => inject(key);
